import {
  MLHFQ_EMOTIONAL_1BASED,
  MLHFQ_ITEMS_PT,
  MLHFQ_OPTIONS,
  MLHFQ_PHYSICAL_1BASED,
  type MLHFQResultPayload,
} from '../tests/MLHFQTest';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

export const getMLHFQSeverityLabel = (total?: number | null): string => {
  if (typeof total !== 'number') return '—';
  if (total <= 23) return 'Boa qualidade de vida — classificação proposta';
  if (total <= 45) return 'Qualidade de vida moderada — classificação proposta';
  return 'Qualidade de vida ruim — classificação proposta';
};

export const getMLHFQSeverityTone = (total?: number | null): string => {
  if (typeof total !== 'number') return 'bg-slate-100 text-slate-700';
  if (total <= 23) return 'bg-emerald-100 text-emerald-700';
  if (total <= 45) return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
};

export const getMLHFQScoreColor = (total?: number | null): string => {
  if (typeof total !== 'number') return '#475569';
  if (total <= 23) return '#10b981';
  if (total <= 45) return '#f59e0b';
  return '#f43f5e';
};

export const getMLHFQInterpretationText = (
  total?: number | null,
  physicalScore?: number | null,
  emotionalScore?: number | null,
): string => {
  if (typeof total !== 'number') {
    return 'Não há dados suficientes para interpretar o MLHFQ.';
  }

  const severity = getMLHFQSeverityLabel(total).toLowerCase();
  const physicalFragment =
    typeof physicalScore === 'number' ? `No domínio físico, a pontuação foi ${physicalScore}/40.` : '';
  const emotionalFragment =
    typeof emotionalScore === 'number' ? `No domínio emocional, a pontuação foi ${emotionalScore}/25.` : '';

  if (total <= 23) {
    return `A classificação proposta por Behlouli et al. sugere ${severity}. ${physicalFragment} ${emotionalFragment}`.trim();
  }

  if (total <= 45) {
    return `A classificação proposta por Behlouli et al. sugere ${severity}. ${physicalFragment} ${emotionalFragment}`.trim();
  }

  return `A classificação proposta por Behlouli et al. sugere ${severity}. ${physicalFragment} ${emotionalFragment}`.trim();
};

const getItemDomain = (itemNumber: number): 'Físico' | 'Emocional' | 'Outros' => {
  if (MLHFQ_PHYSICAL_1BASED.includes(itemNumber as (typeof MLHFQ_PHYSICAL_1BASED)[number])) return 'Físico';
  if (MLHFQ_EMOTIONAL_1BASED.includes(itemNumber as (typeof MLHFQ_EMOTIONAL_1BASED)[number])) return 'Emocional';
  return 'Outros';
};

const getAnswerLabel = (value?: number | null): string => {
  if (typeof value !== 'number') return 'Não respondido';
  return MLHFQ_OPTIONS.find((option) => option.value === value)?.label ?? 'Não respondido';
};

interface MLHFQResultRendererProps {
  payload: Partial<MLHFQResultPayload> | null | undefined;
}

function DomainCard({
  title,
  score,
  maxScore,
  tone,
}: {
  title: string;
  score: number | null;
  maxScore: number;
  tone: string;
}) {
  return (
    <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
      <div className="text-xs font-black uppercase tracking-[0.25em] text-slate-400">{title}</div>
      <div className={`mt-3 text-4xl font-black ${tone}`}>{score ?? '—'}</div>
      <div className="mt-2 text-sm text-slate-500">{score != null ? `${score}/${maxScore}` : 'Pontuação indisponível'}</div>
    </div>
  );
}

export default function MLHFQResultRenderer({ payload }: MLHFQResultRendererProps) {
  const answers = Array.isArray(payload?.answers) ? payload.answers : [];
  const total = typeof payload?.total === 'number' ? payload.total : null;
  const maxScore = typeof payload?.maxScore === 'number' ? payload.maxScore : 105;
  const physicalScore = typeof payload?.physical?.score === 'number' ? payload.physical.score : null;
  const emotionalScore = typeof payload?.emotional?.score === 'number' ? payload.emotional.score : null;
  const otherScore = typeof payload?.other?.score === 'number' ? payload.other.score : null;
  const interpretation = resolveAssessmentInterpretationText(
    payload,
    getMLHFQInterpretationText(total, physicalScore, emotionalScore),
  );

  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="Pontuação MLHFQ"
        value={total ?? '—'}
        valueColor={getMLHFQScoreColor(total)}
        valueCaption={typeof total === 'number' ? `${total}/${maxScore}` : 'Pontuação indisponível'}
        classification={getMLHFQSeverityLabel(total)}
        supportingText="Valores mais altos indicam maior impacto na qualidade de vida."
        interpretation={interpretation}
      >
          <div className="grid gap-4 md:grid-cols-3">
            <DomainCard title="Domínio físico" score={physicalScore} maxScore={40} tone="text-rose-500" />
            <DomainCard title="Domínio emocional" score={emotionalScore} maxScore={25} tone="text-amber-500" />
            <DomainCard title="Outros domínios" score={otherScore} maxScore={40} tone="text-blue-600" />
          </div>
      </QuestionnaireResultHero>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Questão</th>
              <th className="px-6 py-4">Domínio</th>
              <th className="px-6 py-4">Resposta selecionada</th>
              <th className="px-6 py-4 text-center">Pontuação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {MLHFQ_ITEMS_PT.map((question, index) => {
              const score = typeof answers[index] === 'number' && answers[index] >= 0 ? answers[index] : null;
              const domain = getItemDomain(index + 1);
              const domainTone =
                domain === 'Físico' ? 'bg-rose-100 text-rose-700' : domain === 'Emocional' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700';
              return (
                <tr key={`mlhfq-item-${index + 1}`} className="hover:bg-slate-50/60">
                  <td className="px-6 py-4 font-medium text-slate-700">{index + 1}. {question}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-lg px-2.5 py-1 text-[10px] font-black uppercase ${domainTone}`}>
                      {domain}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{getAnswerLabel(score)}</td>
                  <td className="px-6 py-4 text-center font-black text-slate-900">{score ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
